import { createContext, useContext, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@contexts/User.context';
import { getMonthlyProjectsLimit, getUserStorage } from '@utils/api';
import { getExifData } from '@routes/userRoutes/projects/newProject/utils/exif';
import { getImageStatistics } from '@utils/api';
import useSubmitProject from '@routes/userRoutes/projects/newProject/utils/hooks/useSubmitProject';
import { getFileType } from '@routes/userRoutes/projects/newProject/utils/helpers';
import { DEFAULT_MODEL_OPTION } from '@config/videoDetectionModelConfig';

const useIsTif = files => useMemo(() => files.find(file => getFileType(file) == 'tif'), [files]);

const useMonthlyProjectsLimit = () => {
	const { tierPro } = useAuth();
	const { data, error, isLoading } = useQuery({
		queryKey: ['userMonthlyProjectsLimit'],
		queryFn: getMonthlyProjectsLimit,
		enabled: tierPro,
		refetchOnWindowFocus: false,
	});

	if (error) console.error('Error fetching monthly projects limit.', error);

	const {
		permission,
		limit: projectLimit,
		monthly_projects: monthlyProjects,
		image_limit: imageLimit,
		days_left: daysLeft,
	} = data ?? {};

	const showLimitInfo =
		(!!monthlyProjects || monthlyProjects == 0) && (!!projectLimit || projectLimit == 0);

	return {
		error,
		isLoading: tierPro ? isLoading : false,

		showLimitInfo,
		permission,
		projectLimit,
		monthlyProjects,
		projectLimitReached: projectLimit <= monthlyProjects,
		imageLimit,
		daysLeft,
	};
};

const useTotalFilesSize = files =>
	useMemo(() => files.reduce((accumulator, file) => accumulator + file.size, 0), [files]);

const useUserStorage = totalFilesSize => {
	const { tierPro, subscription } = useAuth();
	const { data, error, isLoading } = useQuery({
		queryKey: ['userStorage'],
		queryFn: getUserStorage,
		enabled: tierPro,
		refetchOnWindowFocus: false,
	});

	if (error) console.error('Error fetching user storage.', error);

	const { used: storageUsed } = data ?? {};
	const storageLimit = subscription?.product_metadata?.storage_limit ?? 0;

	return {
		error,
		isLoading: tierPro ? isLoading : false,

		storageUsed,
		storageLimit: parseInt(storageLimit),
		storageLimitReached: storageUsed + totalFilesSize > storageLimit,
	};
};

const useExif = (files, isTif, mode, isSubmitting) => {
	const exifFiles = files.filter(file => ['jpg', 'tif'].includes(getFileType(file)));
	const hasFiles = exifFiles.length > 0;
	const getExif = async () => await getExifData(exifFiles);

	const getExifFootprint = async exifData =>
		await getImageStatistics({
			exifData: exifData,
			isTif: isTif,
			imageMode: mode,
		});

	const {
		data: exifData,
		error: exifError,
		isLoading: exifIsLoading,
	} = useQuery({
		queryKey: ['exif', exifFiles, isTif, mode],
		queryFn: getExif,
		enabled: !isSubmitting && hasFiles,
		refetchOnWindowFocus: false,
		retry: false,
	});

	if (exifError && !isSubmitting) console.warn('Error parsing exif data.', exifError);

	const {
		data: exifFootprint,
		error: exifFootprintError,
		isLoading: exifFootprintIsLoading,
	} = useQuery({
		queryKey: ['exifFootprint', exifFiles, isTif, mode, exifData],
		queryFn: () => getExifFootprint(exifData),
		enabled: !!exifData && !isSubmitting && hasFiles,
		refetchOnWindowFocus: false,
		retry: false,
	});
	if (exifFootprintError)
		console.warn('Error fetching exif footprint from API.', exifFootprintError);

	const error =
		exifError || exifFootprintError
			? { exif: exifError, exifFootprint: exifFootprintError }
			: null;

	const isLoading = hasFiles && !error ? exifIsLoading || exifFootprintIsLoading : false;

	return {
		error,
		isLoading: isLoading && hasFiles,
		exifData,
		exifFootprint,
	};
};

/** Exported for the dev preview page, which provides its own value. */
export const NewProjectContext = createContext(null);
const NewProjectProvider = ({ children }) => {
	const {
		isLoading: isSubmitting,
		progress,
		error: submitError,
		clearError: clearSubmitError,
		submit,
	} = useSubmitProject();

	const [files, setFiles] = useState([]);
	const [mode, setMode] = useState(null);

	const isTif = useIsTif(files);
	const totalFilesSize = useTotalFilesSize(files);
	const monthlyProjectsLimit = useMonthlyProjectsLimit();
	const userStorage = useUserStorage(totalFilesSize);
	const exif = useExif(files, isTif, mode, isSubmitting);

	const methods = useForm({
		defaultValues: {
			projectName: '',
			projectDescription: '',
			projectTags: [],
			fastOrtophoto: false,
			projectModels: '',
			modelOption: DEFAULT_MODEL_OPTION,
		},
	});
	const { handleSubmit } = methods;

	const formatArray = array => array.map(item => item?.value ?? item);
	const onSubmit = formData => {
		// Prevent submission if there are errors in user storage or monthly projects limit
		if (userStorage?.error || monthlyProjectsLimit?.error) return;

		// Project Info
		const projectInfo = {
			projectMode: mode,
			projectName: formData.projectName,
			projectDescription: formData.projectDescription,
			projectTags: formatArray(formData.projectTags),
		};

		const fileInfo = {
			files: files,
			isTif: isTif,
			totalFilesSize: totalFilesSize,
			exifData: exif?.exifData,
		};

		const selectedModel = formData.projectModels.value;
		const advancedOptions = {
			processing_preset: formData?.fastOrthophoto ? 'fast' : 'forest',
			predict_requests: selectedModel ? [{ model_uuid: selectedModel }] : [],
		};

		submit({ projectInfo, fileInfo, advancedOptions });
	};

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<NewProjectContext.Provider
					value={{
						files,
						setFiles,
						mode,
						setMode,
						isSubmitting,
						submitError,
						clearSubmitError,
						progress,
						isTif,
						totalFilesSize,
						monthlyProjectsLimit,
						userStorage,
						exif,
						submit,
						onSubmit,
					}}>
					{children}
				</NewProjectContext.Provider>
			</form>
		</FormProvider>
	);
};

export default NewProjectProvider;

/**
 * Custom hook that provides access to the NewProjectContext.
 * @returns {Object} The NewProjectContext object.
 * @throws {Error} If used outside of a NewProjectProvider.
 */
export const useNewProject = () => {
	const context = useContext(NewProjectContext);
	if (!context) throw new Error('useNewProject must be used within a NewProjectProvider');

	return context;
};
